import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { getTrip } from "lib/api";
import { useInView } from "react-intersection-observer";
import LayoutStay from "components/layouts/LayoutStay";
import Meta from "solar/components/Meta";
import Anchor from "solar/components/Anchor";
import BannerAdventures from "components/shared/stays/BannerAdventures";
import Divider from "components/shared/stays/Divider";
import Core from "components/stay/Core";
import Sidebar from "components/stay/Sidebar";
import Grid from "components/stay/Grid";
import Present from "components/stay/Present";
import PresentHero from "components/stay/PresentHero";
import Navbar from "components/shared/stays/Navbar";
import SurfConditions from "components/shared/stays/SurfConditions";
import StabSubNav from "components/stay/StabSubNav";
import Hero from "components/stay/Hero";
import HeroPhoto from "components/stay/HeroPhoto";
import HeroVideo from "components/stay/HeroVideo";
import HeroGraphic from "components/stay/HeroGraphic";
import Story from "components/stay/Story";
import Gallery from "components/stay/Gallery";
import Overview from "components/stay/Overview";
import StabDescription from "components/stay/StabDescription";
import Accommodation from "components/stay/Accommodation";
import Products from "components/stay/Products";
import AvailabilityFixed from "components/stay/AvailabilityFixed";
import AvailabilityFlexible from "components/stay/AvailabilityFlexible";
import Questions from "components/stay/Questions";
import Actions from "components/stay/Actions";
import MessageButton from "components/shared/stays/MessageButton";
import Message from "components/shared/stays/Message";
import SurfBreaks from "components/stay/SurfBreaks";
import Section from "components/stay/Section";
import Location from "components/stay/Location";

function Trip({ trip }) {
  // Data for Electric Acid Trip
  const hasVideoHero = false;
  const bgVideoSrc = "";
  const bgVideoSmallSrc = "";
  const bgVideoPoster = "";
  const bgVideoSmallPoster = "";
  const bgVideoPosterLqip = "";
  const bgGraphic =
    "https://d30ufokftqbxzm.cloudfront.net/acid_trip_lockup.svg";

  const [polyfillChecked, setPolyfillChecked] = useState(false);

  const mainProducts = trip.products?.filter(
    (p) => p.is_visible === true && p.product_type === "main"
  );

  // rt: we should probably stop tracking this for narrow screens
  const [presentRef, presentInView] = useInView({
    threshold: 0,
    initialInView: true,
  });

  // rt: we should probably stop tracking this for narrow screens
  const [actionsRef, actionsInView] = useInView({
    threshold: 0.8,
    actionsInView: false,
  });

  async function loadPolyfill() {
    if (typeof window.IntersectionObserver === "undefined") {
      await import("intersection-observer");
    }
    setPolyfillChecked(true);
  }

  useEffect(() => {
    loadPolyfill();
  }, []);

  return (
    <>
      <Meta
        title={trip.name}
        description={trip.story_body}
        path={`/stays/${trip.slug}`}
        imageOgUrl="https://www.thermal.travel/meta/acid-og.jpg"
        imageTwitterUrl="https://www.thermal.travel/meta/acid-og.jpg"
        imageType="image/jpeg"
      />
      <div ref={polyfillChecked ? presentRef : undefined}>
        <Present>
          <BannerAdventures />
          <PresentHero>
            <Navbar
              parentPath="/stays"
              parentName="All trips"
              smallPrimary="back"
              smallSecondary="actions"
              bigSecondary="search"
              bigTertiary="links-and-menu"
              bigWide
              borderBottom={false}
              tripID={trip.id}
            />
            <Hero tripID={trip.id}>
              {!hasVideoHero && <HeroPhoto photo={trip.main_image} />}
              {hasVideoHero && (
                <HeroVideo
                  videoSrc={bgVideoSrc}
                  videoSmallSrc={bgVideoSmallSrc}
                  posterSrc={bgVideoPoster}
                  posterSmallSrc={bgVideoSmallPoster}
                  posterLqipSrc={bgVideoPosterLqip}
                />
              )}
              <HeroGraphic svgSrc={bgGraphic} alt={trip.name} />
            </Hero>
          </PresentHero>
        </Present>
      </div>
      <StabSubNav
        lowestProduct={trip.lowest_product}
        presentInView={polyfillChecked ? presentInView : true}
        actionsInView={polyfillChecked ? actionsInView : false}
        tripTypeDescription={trip.trip_type_description}
      >
        <Message
          tripID={trip.id}
          tripName={trip.name}
          guideID={trip.operator?.id}
          guidePrimaryEmployeeFirstName={
            trip.operator.primary_employee.first_name
          }
          guidePrimaryEmployeeImageUrl={
            trip.operator.primary_employee.image_url
          }
        >
          <MessageButton
            size={1}
            guidePrimaryEmployeeFirstName={
              trip.operator.primary_employee.first_name
            }
            guidePrimaryEmployeeImageUrl={
              trip.operator.primary_employee.image_url
            }
          />
        </Message>
      </StabSubNav>
      <Anchor id="overview" />
      <Grid>
        <Core>
          {(trip.overview?.Accommodation ||
            trip.overview?.Duration ||
            trip.address_country) && (
            <Section>
              <Overview
                accommodation={trip.overview?.Accommodation}
                city={trip.address_city}
                country={trip.address_country}
                duration={trip.overview?.Duration}
              />
            </Section>
          )}
          <Section>
            <Divider />
            <Story
              header={trip.story_header}
              body={trip.story_body}
              guide={trip.operator.name}
              teammate=""
            />
          </Section>
          <Section>
            <Divider slim />
            <Gallery gallery={trip.trip_media_images} />
          </Section>
          <Section>
            <Divider />
            <StabDescription />
          </Section>
          <Section>
            <Divider />
          </Section>
          <Section>
            <SurfConditions
              season={trip.region?.seasons}
              conditions={trip.surf_body}
            />
          </Section>
          {trip.trip_breaks.length > 0 && (
            <>
              <Section>
                <Divider />
              </Section>
              <Section padded>
                <SurfBreaks
                  breaks={trip.trip_breaks}
                  isVisible={trip.is_visible}
                />
              </Section>
            </>
          )}
          {trip.accommodation_description &&
            trip.trip_type_description !== "Lodge" && (
              <Section>
                <Divider />
                <Accommodation
                  tripTypeDescription={trip.trip_type_description}
                  description={trip.accommodation_description}
                  images={trip.trip_accommodation_images}
                />
              </Section>
            )}
          {trip.trip_type_description !== "Adventure" && mainProducts && (
            <>
              <Section>
                <Divider />
              </Section>
              <Section padded>
                <Products
                  tripTypeDescription={trip.trip_type_description}
                  products={mainProducts.sort(
                    (a, b) => a.price - b.price || a.name.localeCompare(b.name)
                  )}
                />
              </Section>
            </>
          )}
          {trip.address_lat && trip.address_lng && trip.getting_there && (
            <Section>
              <Divider />
              <Location
                latitude={parseFloat(trip.address_lat, 10)}
                longitude={parseFloat(trip.address_lng, 10)}
                gettingThere={trip.getting_there}
                tripTypeDescription="Lodge" // rt: Hardcoded to trigger the zoom level
                regionBreadcrumb={trip.region_breadcrumb}
              />
            </Section>
          )}
        </Core>
        <Sidebar>
          <div ref={polyfillChecked ? actionsRef : undefined}>
            <Actions
              lowestPrice={trip.lowest_product?.price}
              lowestPricingType={trip.lowest_product?.pricing_type}
              tripID={trip.id}
              tripName={trip.name}
              guideID={trip.operator?.id}
              guidePrimaryEmployeeFirstName={
                trip.operator.primary_employee.first_name
              }
              guidePrimaryEmployeeImageUrl={
                trip.operator.primary_employee.image_url
              }
            />
          </div>
        </Sidebar>
      </Grid>
      {mainProducts &&
        mainProducts.length > 0 &&
        mainProducts[0].date_selection_type === "flexible" && (
          <>
            <Section contained>
              <Divider />
            </Section>
            <Section padded>
              <AvailabilityFlexible
                tripID={trip.id}
                tripName={trip.name}
                tripTypeDescription={trip.trip_type_description}
                guideID={trip.operator?.id}
                guidePrimaryEmployeeFirstName={
                  trip.operator.primary_employee.first_name
                }
                guidePrimaryEmployeeImageUrl={
                  trip.operator.primary_employee.image_url
                }
                products={mainProducts}
                aggregatedUnavailableDates={trip.aggregated_unavailable_dates}
                aggregatedMinNights={trip.aggregated_min_nights}
              />
            </Section>
          </>
        )}
      {mainProducts &&
        mainProducts.length > 0 &&
        mainProducts[0].date_selection_type === "fixed" && (
          <>
            <Section contained>
              <Divider />
            </Section>
            <Section padded>
              <AvailabilityFixed
                tripID={trip.id}
                tripName={trip.name}
                tripTypeDescription={trip.trip_type_description}
                guideID={trip.operator?.id}
                guidePrimaryEmployeeFirstName={
                  trip.operator.primary_employee.first_name
                }
                guidePrimaryEmployeeImageUrl={
                  trip.operator.primary_employee.image_url
                }
                products={mainProducts}
              />
            </Section>
          </>
        )}
      <Section contained>
        <Divider />
        <Questions
          questions={trip.questions}
          cancellationPolicy={trip.cancellation_policy}
        />
      </Section>
    </>
  );
}

export async function getStaticProps() {
  const response = await getTrip("electric-acid-trip");
  if (response.errorMsg) {
    return { notFound: true };
  }
  return {
    props: { trip: response },
    revalidate: 1800,
  };
}

Trip.propTypes = {
  trip: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    slug: PropTypes.string,
    is_visible: PropTypes.bool,
    trip_type_description: PropTypes.string,
    country: PropTypes.string,
    category_list: PropTypes.arrayOf(PropTypes.string),
    airport: PropTypes.shape({
      name: PropTypes.string,
      iata_code: PropTypes.string,
    }),
    region: PropTypes.shape({
      name: PropTypes.string,
      country: PropTypes.string,
      world_area: PropTypes.string,
      seasons: PropTypes.arrayOf(PropTypes.string),
    }),
    address_city: PropTypes.string,
    address_country: PropTypes.string,
    address_lat: PropTypes.string,
    address_lng: PropTypes.string,
    story_header: PropTypes.string,
    story_body: PropTypes.string,
    surf_body: PropTypes.string,
    lowest_product: PropTypes.shape({
      min_guests: PropTypes.number,
      max_guests: PropTypes.number,
      price: PropTypes.string,
      pricing_type: PropTypes.string,
    }),
    overview: PropTypes.objectOf(PropTypes.string),
    questions: PropTypes.objectOf(PropTypes.string),
    getting_there: PropTypes.string,
    accommodation_description: PropTypes.string,
    description: PropTypes.string,
    cancellation_policy: PropTypes.string,
    trip_reviews: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
      })
    ),
    trip_description_images: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string,
        content_type: PropTypes.string,
        byte_size: PropTypes.number,
      })
    ),
    trip_accommodation_images: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string,
        content_type: PropTypes.string,
        byte_size: PropTypes.number,
      })
    ),
    surf_image: PropTypes.shape({
      url: PropTypes.string,
      content_type: PropTypes.string,
      byte_size: PropTypes.number,
    }),
    main_image: PropTypes.shape({
      url: PropTypes.string,
      content_type: PropTypes.string,
      byte_size: PropTypes.number,
    }),
    trip_media_images: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        position: PropTypes.number,
        created_at: PropTypes.string,
        file: PropTypes.shape({
          url: PropTypes.string,
          content_type: PropTypes.string,
          byte_size: PropTypes.number,
        }),
      })
    ),
    operator: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      slug: PropTypes.string,
      story_body: PropTypes.string,
      main_image: PropTypes.shape({
        url: PropTypes.string,
        content_type: PropTypes.string,
        byte_size: PropTypes.number,
      }),
      established_date: PropTypes.string,
      story: PropTypes.string,
      language: PropTypes.string,
      primary_employee: PropTypes.shape({
        first_name: PropTypes.string,
        name: PropTypes.string,
        image_url: PropTypes.string,
      }),
      operator_employees: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          name: PropTypes.string,
          title: PropTypes.string,
          description: PropTypes.string,
          main_image: PropTypes.shape({
            url: PropTypes.string,
            content_type: PropTypes.string,
            byte_size: PropTypes.number,
          }),
        })
      ),
    }),
    products: PropTypes.arrayOf(
      PropTypes.shape({
        product_type: PropTypes.string,
      })
    ),
    trip_breaks: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
      })
    ),
    aggregated_unavailable_dates: PropTypes.arrayOf(PropTypes.string),
    aggregated_min_nights: PropTypes.number,
    region_breadcrumb: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        slug: PropTypes.string,
      })
    ),
  }).isRequired,
};

Trip.Layout = LayoutStay;

export default Trip;
